import Axios, { AxiosError } from 'axios'
import { defineStore } from 'pinia'

import { Nullable } from '@shared/models/types/primary'
import { resourcePath } from '@shared/plugins/api'
import { ApiTransport } from '@shared/plugins/api-transport'
import { getErrorCodes } from '@shared/utils/helpers/errors/error-handling'
import { LsGlobalKeys, lsSave } from '@shared/utils/helpers/local-storage'

import { ApiErrorResponse } from '@protots/response/api_error_response'

import { ApiErrorCode } from '@protos/api_error_code_pb'
import { ChatProps } from '@protos/apps/pc/chat_props_pb'
import { ChatWidget } from '@protos/apps/pc/chat_widget_pb'
import { Conversation } from '@protos/apps/pc/conversation_pb'
import {
  LandingInfo_AuthRequest as AuthRequest,
  LandingInfo,
} from '@protos/apps/pc/landing_info_pb'
import { LeadProps } from '@protos/apps/pc/request/lead_props_pb'
import { PeerProps } from '@protos/auth/peer_props_pb'
import { Session } from '@protos/auth/session_pb'
import { User } from '@protos/auth/user_pb'

import { PlainMessage } from '@bufbuild/protobuf'
import {
  getAuthRequest,
  getAuthSession,
  getLandingErrorCode,
} from '@pc/helpers/session'
import {
  AuthDTO,
  IAuth,
  IRequestAuth,
  RequestAuthDTO,
} from '@pc/models/proto/dto/leads'
import { PatientSessionRO } from '@pc/models/proto/ro/leads'
import { useChatWidgetsStore } from '@pc/store/chat-widgets'
import { useConversationsStore } from '@pc/store/conversations'
import type { JsonValue } from '@protobuf-ts/runtime'

interface State {
  peerProps: Nullable<PeerProps>
  chatProps: Nullable<ChatProps>
  authError: Nullable<ApiErrorCode>
  errorMessage: Nullable<string>
  authSession: Nullable<Session>
  authRequest: Nullable<AuthRequest>
  visitorOverride: boolean
}
const state = (): State => ({
  peerProps: null,
  chatProps: null,
  authError: null,
  errorMessage: null,
  authSession: null,
  authRequest: null,
  visitorOverride: false,
})

export const apiTransport = new ApiTransport(resourcePath, 'pc')

export const apiTransportBaseUrl = new ApiTransport('', '')

export const useSessionStore = defineStore('session', {
  state,

  actions: {
    patchSession(data?: LandingInfo) {
      const patch = {
        peerProps: data?.peerProps,
        chatProps: data?.chatProps,
        authSession: getAuthSession(data?.landingState),
        authRequest: getAuthRequest(data?.landingState),
        authError: getLandingErrorCode(data?.landingState)?.errorCode,
        errorMessage: getLandingErrorCode(data?.landingState)?.errorMessage,
      }

      const session = getAuthSession(data?.landingState)
      if (data?.chatProps) {
        this.setChatData(data?.chatProps)
      }

      if (session) {
        lsSave(LsGlobalKeys.Crd, session)
      }
      if (data?.peerProps) {
        lsSave(LsGlobalKeys.PeerProps, data?.peerProps)
      }

      const keys = Object.keys(patch) as Array<keyof typeof patch>

      keys.forEach((k) => {
        patch[k] ?? delete patch[k]
      })

      this.$patch(patch)
    },
    async fetchToken(hash: string) {
      const { data } =
        await apiTransportBaseUrl.getWithoutRequest<PatientSessionRO>(
          `/s/${hash}`,
          PatientSessionRO
        )

      this.patchSession(data)

      return data
    },
    async requestAuth(params: IRequestAuth) {
      const { data } = await apiTransport.post<
        RequestAuthDTO,
        PatientSessionRO
      >(
        '/request_auth',
        new RequestAuthDTO(params),
        RequestAuthDTO,
        PatientSessionRO
      )

      this.patchSession(data)
    },
    async postAuth(dto: IAuth) {
      try {
        const resp = await apiTransport.post<AuthDTO>(
          '/auth',
          new AuthDTO(dto),
          AuthDTO
        )
        const { data } = PatientSessionRO.fromJson(resp)
        this.patchSession(data)
        return resp
      } catch (error) {
        this.handleAuthError(error)
        throw error
      }
    },

    // Parses and sets error code from API response
    // Looks for first PatientChat Error and uses code for Unknown Error if none are found
    // Will also use code for Unknown Error if this is not an AxiosError
    handleAuthError(error: AxiosError | unknown) {
      if (!Axios.isAxiosError(error)) {
        this.authError = ApiErrorCode.API_ERROR_UNKNOWN
        return
      }

      const { errors } = ApiErrorResponse.fromJson(
        error.response?.data as JsonValue
      )

      const codes = getErrorCodes(errors)
      // 7 is the error code range for PatientChat errors
      const authError = codes.find((code: number) =>
        String(code).startsWith('7')
      )

      if (authError) {
        this.authError = authError
      } else {
        this.authError = ApiErrorCode.API_ERROR_UNKNOWN
      }
    },

    setWidgetData(widget: ChatWidget) {
      if (this.chatProps?.chatWidget) {
        this.chatProps.chatWidget = widget
      }
    },

    setChatData(chatProps: ChatProps) {
      const chatWidgetsStore = useChatWidgetsStore()
      if (chatProps?.chatWidget) {
        chatWidgetsStore.setWidgetData(chatProps?.chatWidget as ChatWidget)
      }
      if (chatProps?.conversation) {
        this.setConversationData(chatProps?.conversation)
      }
    },

    setConversationData(conversation: Conversation) {
      const conversationsStore = useConversationsStore()
      conversationsStore.mergeToConversations(conversation)
    },
  },

  getters: {
    isMe(s) {
      return (id?: User['id']) => s.authSession?.user?.id === id
    },
    leadProps(): PlainMessage<LeadProps> {
      const conversationId = this.chatProps?.conversation?.id ?? 0
      const chatWidgetId = this.chatProps?.chatWidget?.id ?? 0
      const visitant = this.chatProps?.visitant as LeadProps['visitant']

      return {
        campReceiverLogId: 0, // Not Available on this Page
        conversationId,
        chatWidgetId,
        rtcSessionId: 0, // Not Available on this Page
        visitant,
      }
    },
    isKioskModeEnabled: (state) =>
      state.chatProps?.chatWidget?.isKioskModeEnabled ?? false,
  },
})
